const SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js';
/** A stalled script must not leave the form stuck in "sending". */
const TOKEN_TIMEOUT_MS = 10_000;

type RecaptchaApi = {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
    interface Window {
        grecaptcha?: { enterprise?: RecaptchaApi };
    }
}

/** One script per page, shared by every form instance. */
let loading: Promise<RecaptchaApi> | null = null;

/** Injects the reCAPTCHA script once and resolves when its API is ready. */
export function loadRecaptcha(siteKey: string): Promise<RecaptchaApi> {
    loading ??= injectScript(siteKey);
    return loading;
}

/** Fresh token for one submission — tokens are single-use and expire after two minutes. */
export function executeRecaptcha(siteKey: string, action: string): Promise<string> {
    return Promise.race([requestToken(siteKey, action), rejectAfter(TOKEN_TIMEOUT_MS)]);
}

async function requestToken(siteKey: string, action: string): Promise<string> {
    const api = await loadRecaptcha(siteKey);
    return api.execute(siteKey, { action });
}

function rejectAfter(ms: number): Promise<never> {
    return new Promise((_, reject) => setTimeout(() => reject(new Error('reCAPTCHA timed out')), ms));
}

function injectScript(siteKey: string): Promise<RecaptchaApi> {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');

        const fail = (message: string) => {
            // Forget the failed attempt, so the next submit can try again.
            loading = null;
            script.remove();
            reject(new Error(message));
        };

        script.src = `${SCRIPT_URL}?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.onerror = () => fail('reCAPTCHA script failed to load');
        script.onload = () => {
            const api = window.grecaptcha?.enterprise;
            if (!api) return fail('reCAPTCHA API missing');
            api.ready(() => resolve(api));
        };

        document.head.appendChild(script);
    });
}
